import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { authAPI, getErrorMessage } from '../../utils/api';

export default function VerifyEmailLink() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('verifying'); // verifying, success, error

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setLoading(false);
      toast.error('Invalid verification link');
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const response = await authAPI.verifyEmailLink(token);
      setStatus('success');
      toast.success('✓ Email verified successfully!');

      // Clear verification email from storage
      localStorage.removeItem('pendingEmailVerification');

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/auth/login');
      }, 2000);
    } catch (error) {
      console.error('Verification error:', error);
      setStatus('error');
      toast.error(getErrorMessage(error, 'Invalid or expired verification link'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2">
          <CardTitle className="text-2xl text-center">Email Verification</CardTitle>
          <CardDescription className="text-center">
            {loading && 'Verifying your email...'}
            {status === 'success' && 'Email verified successfully!'}
            {status === 'error' && 'Verification failed'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {loading && (
            <div className="text-center py-8">
              <div className="loading-spinner"></div>
              <p className="mt-4 text-sm text-muted-foreground">Please wait while we verify your email...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center py-8">
              <div className="text-6xl mb-4">✓</div>
              <p className="text-lg font-medium text-green-600 mb-2">Email Verified!</p>
              <p className="text-sm text-muted-foreground">Redirecting to login...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center py-8">
              <div className="text-6xl mb-4">✗</div>
              <p className="text-lg font-medium text-red-600 mb-2">Verification Failed</p>
              <p className="text-sm text-muted-foreground mb-4">
                This link may have expired or is invalid. Please request a new verification link.
              </p>
              <button
                onClick={() => navigate('/auth/login')}
                className="inline-flex items-center justify-center h-11 px-6 bg-[#ff2d2d] hover:bg-[#7f1d1d] text-white font-semibold rounded-none shadow-xl transition-all duration-300 transition-ease-in-out"
              >
                Go to Login
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

